import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Download, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { SelectField } from '../../components/ui/SelectField';
import { Spinner } from '../../components/ui/Spinner';
import { listAccounts } from '../accounts';
import {
  createHostedReport,
  deleteHostedReport,
  downloadHostedReport,
  listHostedReports,
  revokeHostedReport,
  saveBlobAsFile,
  unpackUntilReady,
  updateHostedReport,
  uploadBundle,
} from './api';
import {
  BUNDLE_NO_DOWNLOAD_HELP,
  bundleSizeLine,
  bundleStatusBadge,
  type BundlePhaseView,
} from '../../lib/hostedBundleView';
import { CreateHostedReportDialog, type CreateRequest } from './CreateHostedReportDialog';
import {
  AddToPortalDialog,
  listPortalLibrary,
  publishToPortal,
  removeReportFromPortal,
} from '../portalLibrary';
import {
  portalMarkerBadge,
  portalRowAction,
  removeFromPortalSummary,
} from '../../lib/portalLibrary/staffView';
import type { PortalAudienceReach } from '../../types/portalLibrary';
import { EditHostedReportDialog } from './EditHostedReportDialog';
import type { HostedReportPatch } from '../../lib/hostedReportEdit';
import {
  accountOptions,
  creatorOptions,
  filterHostedReports,
  formatDateTime,
  formatExpiry,
  formatFileSize,
  reportState,
  EMPTY_HOSTED_REPORT_FILTERS,
  type HostedReportFilters,
} from '../../lib/hostedReportsView';
import {
  type HostedReportState,
  type HostedReportSummary,
} from '../../types/hostedReport';

// Tools → HTML Hosting — the management surface.
//
// EVERYONE SEES ALL REPORTS, with the creator's name on each row (Scott's
// call): you need to find a colleague's report when they are on leave. Filters
// by client, creator and state are what keep that list navigable.

const STATE_BADGE: Record<HostedReportState, { variant: 'success' | 'neutral' | 'danger'; label: string }> = {
  ok: { variant: 'success', label: 'Live' },
  expired: { variant: 'neutral', label: 'Expired' },
  revoked: { variant: 'danger', label: 'Revoked' },
};

function CopyLinkButton({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => {
        void navigator.clipboard.writeText(url);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      }}
      aria-label="Copy link"
    >
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      {copied ? 'Copied' : 'Copy link'}
    </Button>
  );
}

export function HostedReportsPage() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<HostedReportFilters>(
    EMPTY_HOSTED_REPORT_FILTERS,
  );
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<HostedReportSummary | null>(null);
  const [publishedReach, setPublishedReach] = useState<PortalAudienceReach | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<HostedReportSummary | null>(null);
  const [editing, setEditing] = useState<HostedReportSummary | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  // Round 2c: publishing an EXISTING report, from the row or the Edit dialog.
  const [adopting, setAdopting] = useState<HostedReportSummary | null>(null);
  // Round 6 §1: taking a report's tile OFF the portal, after a confirm.
  const [removing, setRemoving] = useState<HostedReportSummary | null>(null);

  const reportsQuery = useQuery({
    queryKey: ['hosted-reports'],
    queryFn: listHostedReports,
  });
  const accountsQuery = useQuery({
    queryKey: ['accounts'],
    queryFn: listAccounts,
    // Only needed for the create / edit dialogs' account picker.
    enabled: creating || editing !== null || adopting !== null,
  });

  // Round 4 §3: which tiles exist, so a marker pointing at a deleted one reads
  // as "tile missing". The SAME query key as the Portal library page — one
  // cached list, not a second request per page.
  const libraryQuery = useQuery({ queryKey: ['portal-library'], queryFn: listPortalLibrary });
  const entryIds = useMemo(
    () => (libraryQuery.data ? new Set(libraryQuery.data.map((row) => row.entry.id)) : null),
    [libraryQuery.data],
  );

  const reports = useMemo(() => reportsQuery.data ?? [], [reportsQuery.data]);
  const now = Date.now();
  const visible = useMemo(
    () => filterHostedReports(reports, filters, now),
    [reports, filters, now],
  );

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['hosted-reports'] });

  const [bundleProgress, setBundleProgress] = useState<BundlePhaseView | null>(null);
  const [entryChoice, setEntryChoice] = useState<{ reportId: string; candidates: string[] } | null>(null);

  const createMutation = useMutation({
    // A portal publish uploads the file AND creates the tile in one request; the
    // share state then shows the reach it went out to.
    mutationFn: async (req: CreateRequest) => {
      if (req.kind === 'portal') {
        const { kind: _kind, ...args } = req;
        const res = await publishToPortal(args);
        return { report: res.report, reach: res.reach };
      }
      if (req.kind === 'bundle') {
        const { kind: _kind, file, ...meta } = req;
        setEntryChoice(null);
        const res = await uploadBundle(
          { ...meta, filename: file.name, sizeBytes: file.size, file },
          setBundleProgress,
        );
        if ('needsEntry' in res) {
          // The zip is uploaded and waiting: staff pick the page, then it unpacks.
          setEntryChoice(res.needsEntry);
          return null;
        }
        return { report: res.report, reach: null };
      }
      const { kind: _kind, ...args } = req;
      return { report: await createHostedReport(args), reach: null };
    },
    onSuccess: (result) => {
      if (!result) return;
      const { report, reach } = result;
      setBundleProgress(null);
      setCreated(report);
      setPublishedReach(reach);
      setCreateError(null);
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['portal-library'] });
    },
    onError: (err: Error) => {
      setBundleProgress(null);
      setCreateError(err.message);
    },
  });

  const chooseEntryMutation = useMutation({
    mutationFn: async ({ reportId, path }: { reportId: string; path: string }) =>
      unpackUntilReady(reportId, path, setBundleProgress),
    onSuccess: (res) => {
      if ('needsEntry' in res) return;
      setEntryChoice(null);
      setBundleProgress(null);
      setCreated(res.report);
      setCreateError(null);
      invalidate();
    },
    onError: (err: Error) => {
      setBundleProgress(null);
      setCreateError(err.message);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: revokeHostedReport,
    onSuccess: () => {
      toast.success('Link revoked.');
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const editMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: HostedReportPatch }) =>
      updateHostedReport(id, patch),
    onSuccess: () => {
      toast.success('Changes saved — the link now serves them.');
      setEditing(null);
      setEditError(null);
      invalidate();
    },
    // Kept in the dialog rather than a toast: the dialog stays open on failure,
    // so the AM's typed changes aren't lost behind a message that vanishes.
    onError: (err: Error) => setEditError(err.message),
  });

  // Pull our own hosted file back down. ⚠️ Not a link: the endpoint is behind
  // requireAuth and an anchor navigation carries no Firebase token, so it
  // fetches with the auth header and saves the blob.
  const downloadMutation = useMutation({
    mutationFn: downloadHostedReport,
    onSuccess: ({ blob, filename }) => saveBlobAsFile(blob, filename),
    onError: (err: Error) => toast.error(err.message),
  });

  const removeMutation = useMutation({
    mutationFn: (r: HostedReportSummary) => removeReportFromPortal(r.id),
    onSuccess: ({ removed }) => {
      toast.success(removed === 'tile' ? 'Taken off the portal.' : 'Portal marker cleared.');
      setRemoving(null);
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['portal-library'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteHostedReport,
    onSuccess: () => {
      toast.success('Report deleted.');
      setConfirmDelete(null);
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div>
      <PageHeader
        title="HTML Hosting"
        description="Host a self-contained HTML report and share a link with a client."
        actions={
          <Button
            onClick={() => {
              setCreated(null);
              setCreateError(null);
              setCreating(true);
            }}
          >
            <Plus className="h-4 w-4" />
            Host a report
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-2">
        <Input
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          placeholder="Search title, client or creator…"
          className="h-9 w-64"
          aria-label="Search reports"
        />
        <SelectField
          value={filters.accountId ?? ''}
          onValueChange={(v) => setFilters({ ...filters, accountId: v || null })}
          options={[{ value: '', label: 'All clients' }, ...accountOptions(reports)]}
          aria-label="Filter by client"
          size="sm"
        />
        <SelectField
          value={filters.createdBy ?? ''}
          onValueChange={(v) => setFilters({ ...filters, createdBy: v || null })}
          options={[{ value: '', label: 'All creators' }, ...creatorOptions(reports)]}
          aria-label="Filter by creator"
          size="sm"
        />
        <SelectField
          value={filters.state ?? ''}
          onValueChange={(v) =>
            setFilters({ ...filters, state: (v || null) as HostedReportState | null })
          }
          options={[
            { value: '', label: 'Any status' },
            { value: 'ok', label: 'Live' },
            { value: 'expired', label: 'Expired' },
            { value: 'revoked', label: 'Revoked' },
          ]}
          aria-label="Filter by status"
          size="sm"
        />
      </div>

      {reportsQuery.isLoading && <Spinner />}
      {reportsQuery.isError && (
        <p className="text-sm text-destructive">
          {(reportsQuery.error as Error).message}
        </p>
      )}

      {!reportsQuery.isLoading && visible.length === 0 && (
        <p className="text-sm text-slate-500">
          {reports.length === 0
            ? 'No reports hosted yet.'
            : 'No reports match those filters.'}
        </p>
      )}

      {visible.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-slate-200">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2 font-medium">Report</th>
                <th className="px-3 py-2 font-medium">Client</th>
                <th className="px-3 py-2 font-medium">Created by</th>
                <th className="px-3 py-2 font-medium">Expires</th>
                <th className="px-3 py-2 font-medium">Views</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                const state = reportState(r, now);
                const badge = STATE_BADGE[state];
                const downloading =
                  downloadMutation.isPending && downloadMutation.variables?.id === r.id;
                return (
                  <tr key={r.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900">{r.title}</div>
                      <div className="text-xs text-slate-500">
                        {bundleSizeLine(r) ?? formatFileSize(r.sizeBytes)}
                        {r.hasPassword && ' · password protected'}
                        {r.lastReplacedAt &&
                          ` · file replaced ${formatDateTime(r.lastReplacedAt)}`}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{r.accountName ?? '—'}</td>
                    <td className="px-3 py-2 text-slate-600">
                      <div>{r.createdByName}</div>
                      <div className="text-xs text-slate-500">
                        {formatDateTime(r.createdAt)}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      {formatExpiry(r.expiresAt)}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{r.viewCount}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap items-center gap-1">
                        <Badge variant={badge.variant}>{badge.label}</Badge>
                        {(() => {
                          const b = bundleStatusBadge(r);
                          return b ? (
                            <Badge variant="outline" title={b.help}>
                              {b.label}
                            </Badge>
                          ) : null;
                        })()}
                        {/* ⚠️ The pointer, not a verified live tile — it can be stranded. */}
                        {(() => {
                          const marker = portalMarkerBadge(r.portalLibraryEntryId, entryIds);
                          return marker ? (
                            <Badge variant={marker.variant} title={marker.help}>
                              {marker.label}
                            </Badge>
                          ) : null;
                        })()}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <CopyLinkButton url={r.url} />
                        {/*
                          ⚠️ A pending state is not polish here: a 9.4 MB report
                          on a slow connection is several silent seconds, and a
                          button that looks inert gets clicked again.
                        */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => downloadMutation.mutate(r)}
                          disabled={downloading || r.kind === 'bundle'}
                          title={r.kind === 'bundle' ? BUNDLE_NO_DOWNLOAD_HELP : undefined}
                        >
                          <Download className="h-4 w-4" />
                          {downloading ? 'Downloading…' : 'Download'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditError(null);
                            setEditing(r);
                          }}
                        >
                          Edit
                        </Button>
                        {/*
                          Round 2c. ⚠️ ALWAYS ENABLED, deliberately. The server's
                          refusal is the authority (revoked, a LIVE tile already
                          using the file, a password with no tick) and it says
                          why in the dialog. A client-side disable keyed on the
                          marker also blocked a STRANDED marker (its tile gone),
                          which the server adopts correctly — the UI was stricter
                          than the rule. "In portal" is a row STATE, not this label.
                        */}
                        {/*
                          ⚠️ Round 6 §1: ONE button, two actions, chosen by the
                          MARKER (portalRowAction). A report in the portal gets
                          "Remove from portal" — which also repairs a stranded
                          marker. The label names what the click DOES; the state
                          stays on the badge above.
                        */}
                        {portalRowAction(r.portalLibraryEntryId).action === 'add' ? (
                          <Button variant="ghost" size="sm" onClick={() => setAdopting(r)}>
                            {portalRowAction(r.portalLibraryEntryId).label}
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" onClick={() => setRemoving(r)}>
                            {portalRowAction(r.portalLibraryEntryId).label}
                          </Button>
                        )}
                        {state !== 'revoked' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => revokeMutation.mutate(r.id)}
                            disabled={revokeMutation.isPending}
                          >
                            Revoke
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setConfirmDelete(r)}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <CreateHostedReportDialog
          accounts={accountsQuery.data ?? []}
          busy={createMutation.isPending || chooseEntryMutation.isPending}
          errorText={createError}
          created={created}
          publishedReach={publishedReach}
          progress={bundleProgress}
          entryChoice={entryChoice}
          onChooseEntry={(reportId, path) => chooseEntryMutation.mutate({ reportId, path })}
          onCreate={(req) => createMutation.mutate(req)}
          onClose={() => {
            setCreating(false);
            setCreated(null);
            setPublishedReach(null);
            setBundleProgress(null);
            setEntryChoice(null);
          }}
        />
      )}

      {editing && (
        <EditHostedReportDialog
          report={editing}
          accounts={accountsQuery.data ?? []}
          busy={editMutation.isPending}
          errorText={editError}
          onSave={(patch) => editMutation.mutate({ id: editing.id, patch })}
          onClose={() => {
            setEditing(null);
            setEditError(null);
          }}
          onAddToPortal={() => {
            setAdopting(editing);
            setEditing(null);
            setEditError(null);
          }}
          // Round 6 §1: the SAME confirm the row opens — one dialog, two doors.
          onRemoveFromPortal={() => {
            setRemoving(editing);
            setEditing(null);
            setEditError(null);
          }}
        />
      )}

      {adopting && (
        <AddToPortalDialog
          report={adopting}
          accounts={accountsQuery.data ?? []}
          onClose={() => setAdopting(null)}
        />
      )}

      {removing && (
        <Dialog open onOpenChange={(open) => !open && setRemoving(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Remove “{removing.title}” from the portal?</DialogTitle>
              <DialogDescription>
                {removeFromPortalSummary(
                  libraryQuery.data?.find((row) => row.entry.id === removing.portalLibraryEntryId) ??
                    null,
                  libraryQuery.data !== undefined,
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRemoving(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={removeMutation.isPending}
                onClick={() => removeMutation.mutate(removing)}
              >
                {removeMutation.isPending ? 'Removing…' : 'Remove from portal'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {confirmDelete && (
        <Dialog open onOpenChange={(open) => !open && setConfirmDelete(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Delete this report?</DialogTitle>
              <DialogDescription>
                “{confirmDelete.title}” and its uploaded file will be removed, and the
                link will stop working. This can’t be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDelete(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate(confirmDelete.id)}
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete report'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
